"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslations } from "next-intl"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { Loader2, Check, AlertCircle } from "lucide-react"
import { motion } from "framer-motion"
import SearchableDropdown from "@/components/ui/SearchableDropdown"
import type { Option } from "@/components/ui/SearchableDropdown"
import { CreationFlowInlinePicker } from "@/components/publications/creation-flow/inline-enum-picker"
import { shouldUseClassicPicker } from "@/lib/dropdown-constants"
import type { StepProps, CardListingDraft, CardGradingDetails, CardCentering, CardSurfaceDefects } from "@/types/listing"
import { EMPTY_SPECIAL_ATTRIBUTES } from "@/types/listing"
import {
  creationFlowBlockClass,
  creationFlowInputClass,
  creationFlowSectionLabelClass,
  creationFlowMutedClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"

interface TaxonomyItem { id: string; slug: string | null; name: string; sortOrder: number }
interface GradeScale { id: string; gradeValue: number; gradeLabel: string; isAuthOnly: boolean }

function dedupeTaxonomyItems(items: TaxonomyItem[]): TaxonomyItem[] {
  const seen = new Set<string>()
  const deduped: TaxonomyItem[] = []
  for (const item of items) {
    const normalizedName = item.name.trim().replace(/\s+/g, " ").toLowerCase()
    const normalizedSlug = (item.slug ?? "").trim().toLowerCase()
    const key = normalizedName || normalizedSlug
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(item)
  }
  return deduped
}

function dedupeGradeScales(items: GradeScale[]): GradeScale[] {
  const seen = new Set<string>()
  const deduped: GradeScale[] = []
  for (const item of items) {
    const key = `${item.gradeValue}::${item.gradeLabel.trim().toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(item)
  }
  return deduped
}

const DEFECT_KEYS: CardSurfaceDefects[number][] = [
  "scratches", "holo_damage", "print_line", "silvering", "crease", "whitening", "stain", "indentation",
]

const SEALED_FORMATS = new Set(["booster", "box", "etb", "deck"])

export function CardConditionStep({
  data,
  updateDataAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
}: StepProps) {
  const t = useTranslations("cardCreation.condition")
  const draft = data.cardDraft
  const format = draft?.format
  const isSealed = format ? SEALED_FORMATS.has(format) : false

  const [isGraded, setIsGraded] = useState(draft?.isGraded ?? false)

  const [conditions, setConditions] = useState<TaxonomyItem[]>([])
  const [selectedCondition, setSelectedCondition] = useState<string | undefined>(draft?.conditionId)

  const [gradingCompanies, setGradingCompanies] = useState<TaxonomyItem[]>([])
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(draft?.grading?.companyId)
  const [selectedCompanySlug, setSelectedCompanySlug] = useState<string | null>(null)

  const [gradeScales, setGradeScales] = useState<GradeScale[]>([])
  const [selectedGrade, setSelectedGrade] = useState<string | undefined>(draft?.grading?.gradeId)
  const [certNumber, setCertNumber] = useState(draft?.grading?.certNumber ?? "")
  const [gradesLoading, setGradesLoading] = useState(false)

  const [bgsCentering, setBgsCentering] = useState(draft?.grading?.bgsSubgrades?.centering?.toString() ?? "")
  const [bgsCorners, setBgsCorners] = useState(draft?.grading?.bgsSubgrades?.corners?.toString() ?? "")
  const [bgsEdges, setBgsEdges] = useState(draft?.grading?.bgsSubgrades?.edges?.toString() ?? "")
  const [bgsSurface, setBgsSurface] = useState(draft?.grading?.bgsSubgrades?.surface?.toString() ?? "")

  const [leftPct, setLeftPct] = useState(draft?.centering?.leftPct ?? 50)
  const [topPct, setTopPct] = useState(draft?.centering?.topPct ?? 50)

  const [defects, setDefects] = useState<CardSurfaceDefects>(draft?.surfaceDefects ?? [])

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      fetch("/api/taxonomy?type=card_condition&limit=100").then((r) => r.json()),
      fetch("/api/taxonomy?type=grading_company&limit=100").then((r) => r.json()),
    ]).then(([condRes, compRes]) => {
      if (cancelled) return
      const condItems = Array.isArray(condRes.items) ? condRes.items : []
      const compItems = Array.isArray(compRes.items) ? compRes.items : []
      setConditions(dedupeTaxonomyItems(condItems.sort((a: TaxonomyItem, b: TaxonomyItem) => a.sortOrder - b.sortOrder)))
      setGradingCompanies(dedupeTaxonomyItems(compItems.sort((a: TaxonomyItem, b: TaxonomyItem) => a.sortOrder - b.sortOrder)))
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!selectedCompany) {
      setGradeScales([])
      setSelectedGrade(undefined)
      return
    }
    let cancelled = false
    setGradesLoading(true)
    fetch(`/api/taxonomy/grade-scales?companyId=${selectedCompany}`)
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return
        const items = Array.isArray(res.items) ? res.items : []
        setGradeScales(dedupeGradeScales(items))
        setGradesLoading(false)
      })
      .catch(() => { if (!cancelled) setGradesLoading(false) })
    return () => { cancelled = true }
  }, [selectedCompany])

  const isBgs = selectedCompanySlug === "bgs"
  const bgsAllOrNone = (() => {
    const filled = [bgsCentering, bgsCorners, bgsEdges, bgsSurface].filter((v) => v.trim() !== "")
    return filled.length === 0 || filled.length === 4
  })()

  const noGradesAvailable = !gradesLoading && selectedCompany && gradeScales.length === 0
  const isValid = (() => {
    if (isSealed) return true
    if (isGraded) {
      if (noGradesAvailable) return Boolean(selectedCompany && bgsAllOrNone)
      return Boolean(selectedCompany && selectedGrade && bgsAllOrNone)
    }
    return Boolean(selectedCondition)
  })()

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    let grading: CardGradingDetails | undefined
    if (isGraded && selectedCompany) {
      grading = {
        companyId: selectedCompany,
        gradeId: selectedGrade,
        certNumber: certNumber.trim() || undefined,
      }
      if (isBgs && bgsCentering && bgsCorners && bgsEdges && bgsSurface) {
        grading.bgsSubgrades = {
          centering: parseFloat(bgsCentering),
          corners: parseFloat(bgsCorners),
          edges: parseFloat(bgsEdges),
          surface: parseFloat(bgsSurface),
        }
      }
    }

    let centering: CardCentering | undefined
    if (!isGraded && !isSealed && (leftPct !== 50 || topPct !== 50)) {
      centering = { leftPct, topPct }
    }

    const cardDraft: CardListingDraft = {
      ...(draft ?? {
        cardCategory: "trading_card" as const,
        cardName: "",
        format: "single" as const,
        specialAttributes: { ...EMPTY_SPECIAL_ATTRIBUTES },
        isGraded: false,
      }),
      isGraded,
      conditionId: isGraded || isSealed ? undefined : selectedCondition,
      grading,
      centering,
      surfaceDefects: defects.length > 0 ? defects : undefined,
    }

    updateDataAction({ cardDraft })
  }, [
    draft, isGraded, isSealed, selectedCondition, selectedCompany, selectedGrade,
    certNumber, isBgs, bgsCentering, bgsCorners, bgsEdges, bgsSurface,
    leftPct, topPct, defects, updateDataAction,
  ])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  const toggleDefect = (d: CardSurfaceDefects[number]) => {
    setDefects((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    )
  }

  const companyOptions: Option[] = useMemo(
    () => gradingCompanies.map((c) => ({ code: c.id, name: c.name, flag: "" })),
    [gradingCompanies]
  )
  const selectedCompanyOption: Option | null = useMemo(() => {
    if (!selectedCompany) return null
    const c = gradingCompanies.find((x) => x.id === selectedCompany)
    return c ? { code: c.id, name: c.name, flag: "" } : null
  }, [selectedCompany, gradingCompanies])

  const useInlineCompanyPicker = shouldUseClassicPicker(gradingCompanies.length)

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const sectionLabel = creationFlowSectionLabelClass
  const blockBase = creationFlowBlockClass
  const blockPadding = compact ? "p-3" : "p-4"
  const inputBase = creationFlowInputClass
  const mutedText = creationFlowMutedClass
  const badge = (active: boolean) =>
    cn(
      "rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer select-none transition-colors",
      active
        ? "bg-foreground/15 text-foreground ring-1 ring-foreground/30"
        : "bg-muted/60 text-muted-foreground hover:bg-muted"
    )

  if (isSealed) {
    return (
      <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
        <div className={cn("rounded-2xl", blockBase, blockPadding, "flex items-center gap-3")}>
          <Check className="h-5 w-5 text-green-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">{t("sealedMint")}</p>
            <p className={cn("text-xs", mutedText)}>{t("sealedDesc")}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <motion.div className={compact ? "space-y-4" : "space-y-5"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

        <div className={cn("rounded-2xl", blockBase, blockPadding)}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{t("professionallyGraded")}</p>
              <p className={cn("text-xs", mutedText)}>{t("gradedToggleDesc")}</p>
            </div>
            <div>
              <Switch checked={isGraded} onCheckedChange={setIsGraded} />
            </div>
          </div>
        </div>

        {!isGraded && (
          <>
            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("rawCondition")}</p>
              <div className="grid grid-cols-3 gap-2">
                {conditions.map((c) => {
                  const isSelected = selectedCondition === c.id
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCondition(c.id)}
                      className={cn(
                        "rounded-xl px-3 py-3 text-center text-sm font-semibold transition-colors",
                        isSelected
                          ? "bg-sheet-handle text-foreground ring-1 ring-foreground/30"
                          : "bg-muted/50 text-foreground/80 hover:bg-muted"
                      )}
                    >
                      {c.name}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("centering")}</p>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn("text-xs", mutedText)}>{t("leftRight")}</span>
                    <span className="text-sm font-medium text-foreground">{leftPct}/{100 - leftPct}</span>
                  </div>
                  <div>
                    <Slider value={[leftPct]} min={50} max={90} step={1} onValueChange={([v]) => setLeftPct(v)} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className={cn("text-xs", mutedText)}>{t("topBottom")}</span>
                    <span className="text-sm font-medium text-foreground">{topPct}/{100 - topPct}</span>
                  </div>
                  <div>
                    <Slider value={[topPct]} min={50} max={90} step={1} onValueChange={([v]) => setTopPct(v)} />
                  </div>
                </div>
              </div>
            </div>

            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("surfaceDefects")}</p>
              <div className="flex flex-wrap gap-2">
                {DEFECT_KEYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDefect(d)}
                    className={badge(defects.includes(d))}
                  >
                    {t(`defects.${d}`)}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {isGraded && (
          <>
            <div className={cn("rounded-2xl", blockBase, blockPadding)}>
              <p className={cn(sectionLabel, "mb-3")}>{t("gradingCompany")}</p>
              {useInlineCompanyPicker ? (
                <CreationFlowInlinePicker
                  items={gradingCompanies.map((c) => ({ id: c.id, name: c.name }))}
                  selectedId={selectedCompany}
                  onSelect={(id) => {
                    setSelectedCompany(id)
                    const comp = gradingCompanies.find((c) => c.id === id)
                    setSelectedCompanySlug(comp?.slug ?? null)
                    setSelectedGrade(undefined)
                  }}
                />
              ) : (
                <SearchableDropdown
                  options={companyOptions}
                  label="name"
                  selectedValue={selectedCompanyOption}
                  onSelect={(opt) => {
                    setSelectedCompany(opt.code)
                    const comp = gradingCompanies.find((c) => c.id === opt.code)
                    setSelectedCompanySlug(comp?.slug ?? null)
                    setSelectedGrade(undefined)
                  }}
                  placeholder={t("selectCompany")}
                  showFlag={false}
                  searchPlaceholder={t("searchCompanies")}
                  emptyMessage={t("noCompaniesFound")}
                  sheetTitle={t("selectCompany")}
                />
              )}
            </div>

            {selectedCompany && (
              <div className={cn("rounded-2xl", blockBase, blockPadding)}>
                <p className={cn(sectionLabel, "mb-3")}>{t("grade")}</p>
                {gradesLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : gradeScales.length === 0 ? (
                  <p className={cn("text-sm", mutedText)}>{t("noGrades")}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {gradeScales.map((gs) => {
                      const isSelected = selectedGrade === gs.id
                      return (
                        <button
                          key={gs.id}
                          type="button"
                          onClick={() => setSelectedGrade(gs.id)}
                          className={cn(
                            "rounded-xl px-2 py-2.5 text-center transition-colors",
                            isSelected
                              ? "bg-sheet-handle text-foreground ring-1 ring-foreground/30"
                              : "bg-muted/50 text-foreground/80 hover:bg-muted"
                          )}
                        >
                          <span className="block text-sm font-bold">{gs.gradeValue}</span>
                          <span className={cn("block text-[10px]", mutedText)}>{gs.gradeLabel}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {selectedCompany && (
              <div className={cn("rounded-2xl", blockBase, blockPadding)}>
                <p className={cn(sectionLabel, "mb-3")}>{t("certNumber")} <span className={cn("text-xs font-normal normal-case", mutedText)}>{t("certRecommended")}</span></p>
                <Input
                  placeholder={t("certPlaceholder")}
                  value={certNumber}
                  onChange={(e) => setCertNumber(e.target.value)}
                  className={inputBase}
                />
              </div>
            )}

            {isBgs && selectedCompany && (
              <div className={cn("rounded-2xl", blockBase, blockPadding)}>
                <p className={cn(sectionLabel, "mb-3")}>{t("bgsSubScores")}</p>
                {!bgsAllOrNone && (
                  <div className="flex items-center gap-2 mb-2 text-xs text-amber-600 dark:text-amber-400">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {t("bgsAllOrNone")}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { label: "Centering", val: bgsCentering, set: setBgsCentering },
                    { label: "Corners", val: bgsCorners, set: setBgsCorners },
                    { label: "Edges", val: bgsEdges, set: setBgsEdges },
                    { label: "Surface", val: bgsSurface, set: setBgsSurface },
                  ] as const).map(({ label, val, set }) => (
                    <div key={label}>
                      <Label className="text-xs mb-1 block text-muted-foreground">{label}</Label>
                      <Input
                        type="text" inputMode="decimal"
                        placeholder="0.0"
                        value={val}
                        onChange={(e) => {
                          const v = e.target.value
                          if (v === "" || /^[0-9]*\.?[0-9]?$/.test(v)) set(v)
                        }}
                        className={inputBase}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  )
}
